// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseAvatar,
  BaseFilters,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
} from '~/components/common';
// Others
import { usePermissions } from '~/utils/hooks/userPermissions';
import { QUOTE_PERMISSIONS } from '~/utils/constants/permissions';
import { IClientQuote, IParamDetailClient, IQueryDetailClient } from '~/utils/interface/client';
import { IBaseResponse, IListDataResponse, IPaginationResponse } from '~/utils/interface/base';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_QUOTE,
  EMPTY_STRING,
  FILTER_DEFAULT_VALUES,
  optionsStatusQuotes,
} from '~/utils/constants/common';
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import { StorageEnum } from '~/utils/enum/base';
import { deleteQuote } from '~/thunks/quote/quoteThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailClient } from '~/thunks/client/clientThunk';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { quoteActions, selectIsRefreshListQuote } from '~/thunks/quote/quoteSlice';
import { BusinessTypeEnum, DateFormatEnum, RoleEnum, TypeDetailClientEnum } from '~/utils/enum/common';
import { formatCurrencyUSD, formatDate, getFullName } from '~/utils/helper/common';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
// Styles, images, icons
import styles from './QuoteClientDetailTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsQuotes = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (data: IClientQuote) => void,
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IClientQuote>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'id',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('client_detail_quote_title_quote_id')}</Box>
          <SortComponent name='id' sortType={getSortType('id')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IClientQuote) => (
        <Typography className={cx('textId')} onClick={() => onView(record)}>{`#${record?.id}`}</Typography>
      ),
      minWidth: 60,
    },
    {
      id: 'type',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('client_detail_quote_title_type')}</Box>
          <SortComponent name='type' sortType={getSortType('type')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IClientQuote) => {
        return <BaseStatus label={record?.type} />;
      },
    },
    {
      id: 'startDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('client_detail_quote_title_start_date')}</Box>
          <SortComponent name='start date' sortType={getSortType('start date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IClientQuote) => {
        return <Typography>{formatDate(record?.startDate, DateFormatEnum.M_D_YYYY)}</Typography>;
      },
      minWidth: 140,
    },
    {
      id: 'endDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('client_detail_quote_title_end_date')}</Box>
          <SortComponent name='end date' sortType={getSortType('end date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IClientQuote) => {
        return <Typography>{formatDate(record.endDate, DateFormatEnum.M_D_YYYY)}</Typography>;
      },
      minWidth: 140,
    },
    {
      id: 'createdBy',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('client_detail_quote_title_created_by')}</Box>
          <SortComponent name='created by' sortType={getSortType('created by')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IClientQuote) => {
        return (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.createdBy} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.createdBy?.firstName,
                  lastName: record?.createdBy?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.createdBy?.email}</Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      id: 'quoteAmount',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('client_detail_quote_title_quote_amount')}</Box>
          <SortComponent name='quote amount' sortType={getSortType('quote amount')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IClientQuote) => {
        return <Typography className={cx('textPrice')}>{formatCurrencyUSD({ value: record?.total })}</Typography>;
      },
      minWidth: 160,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IClientQuote) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    {
      id: 'action',
      label: t('client_detail_quote_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IClientQuote) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record),
                },
              ]
            : []),
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_archive', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const QuoteClientDetailTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  const { canDetailQuotes, canEditQuotes, canDeleteQuotes } = usePermissions(QUOTE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: clientId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const isRefreshQuoteList = useSelector(selectIsRefreshListQuote);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_QUOTE);
  const [filterParams, setFilterParams] = useState<IQueryDetailClient>(FILTER_DEFAULT_VALUES);

  const [paramObject, setParamObject] = useState<IQueryDetailClient>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [quoteList, setQuoteList] = useState<IClientQuote[]>([]);
  const [idQuote, setIdQuote] = useState<string>(EMPTY_STRING);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IParamDetailClient = {
      clientId: clientId || EMPTY_STRING,
      type: TypeDetailClientEnum.QUOTES,
      query: paramObject,
    };

    handleGetListQuote(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshQuoteList) return;

    const params: IParamDetailClient = {
      clientId: clientId || EMPTY_STRING,
      type: TypeDetailClientEnum.QUOTES,
      query: paramObject,
    };

    handleGetListQuote(params);

    dispatch(quoteActions.setRefreshQuoteList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshQuoteList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListQuote = (params: IParamDetailClient) => {
    setIsLoading(true);

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        const { data } = res as IBaseResponse<IListDataResponse<IClientQuote[]>>;

        if (!data) return;

        const { responses: listQuote, pagination } = data;

        if (!listQuote) return;

        setQuoteList(listQuote);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAdd = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(`${salesAdminRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(`${salesPersonRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(`${operationsManagerRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(`${warehouseLeadRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(`${warehouseStaffRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.FOREMAN:
        navigate(`${foremanRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.createQuotes}?clientId=${clientId}`);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateQuote(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateQuote(id));
        break;
    }
  };

  const handleCloseConfirmDelete = () => {
    setIdQuote(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdQuote(id);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteQuote(idQuote))
      .unwrap()
      .then(() => {
        setIdQuote(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(quoteActions.setRefreshQuoteList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleView = (data: IClientQuote) => {
    if (data?.type === BusinessTypeEnum.QUOTE) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.detailQuote(data?.id));
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.detailQuote(data?.id));
          break;
      }
    }

    if (data?.type === BusinessTypeEnum.ORDER) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.detailOrder(data?.id));
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.detailOrder(data?.id));
          break;
      }
    }
  };

  const handleFilterStatusChange = (
    quoteStatus: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryDetailClient>) => void
  ) => {
    if (!quoteStatus) return;

    const { value } = quoteStatus;

    onChange?.({ name: 'quoteStatus', value: value });
    setFilterParams({ ...filterParams, quoteStatus: value });
  };

  const handleApplyFilter = (values: IQueryDetailClient | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        quoteStatus: values?.quoteStatus,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_PAGE,
      quoteStatus: undefined,
    });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsStatusQuotes,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, quoteSortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };
  //#endregion Handle Function

  return (
    <Stack id='quoteClientDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <BaseToolbar title={t('client_detail_quote_title_client_orders')}>
          <BaseFilters
            value={filterParams}
            defaultValue={FILTER_DEFAULT_VALUES}
            onApply={handleApplyFilter}
            onClear={handleClearFilter}
          >
            {({ value, onChange }) => (
              <Stack direction={'column'} width={340} mb={4} gap={6}>
                <BaseSelect
                  gap={10}
                  height={40}
                  styleType='contained'
                  label={t('client_detail_quote_title_status')}
                  placeholder={t('client_detail_quote_title_all_status')}
                  callApiFn={fetchOptionFilter}
                  value={value?.status}
                  onChange={(option) => handleFilterStatusChange(option as any, onChange)}
                />
              </Stack>
            )}
          </BaseFilters>

          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAdd}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('client_detail_quote_title_new_quote')}
            </Button>
          </Box>
        </BaseToolbar>
      </Stack>

      <Box component={'div'} className={cx('body')}>
        <BaseTable<IClientQuote>
          dataSource={quoteList || []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsQuotes(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleDelete,
            canDetailQuotes,
            canEditQuotes,
            canDeleteQuotes
          )}
          onPageChange={handlePageChange}
          // {...(canDetailQuotes ? { onRowClick: (_id, record) => handleView(record) } : {})}
        />
      </Box>

      {isLoading && <Loading />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_archive', { ns: 'common/base' })}
          textConfirm={t('common_action_archive', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Stack>
  );
};

export default QuoteClientDetailTab;
